import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip,
} from 'recharts';
import { getProfile, updateMyProfile, toggleBookmark, getProfilesByTag, getRankComparison, getDeepStats, getCoachingStatus, getReferralInfo } from '../services/api';
import ArchetypeBadge from './ArchetypeBadge';
import ProfileCard from './ProfileCard';
import LinkedPlatforms from './LinkedPlatforms';
import ProfileBadges from './ProfileBadges';
import TrainingPlan from './TrainingPlan';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

function DiscordIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}
function SteamIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.455-.397.957-1.494 1.411-2.455 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z"/>
    </svg>
  );
}
function TwitchIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  );
}

function PlatformBadges({ platforms = {} }) {
  const items = [
    platforms.discord?.username  && { label: 'Discord', display: platforms.discord.username,  Icon: DiscordIcon, color: '#5865F2' },
    platforms.steam?.displayName && { label: 'Steam',   display: platforms.steam.displayName,  Icon: SteamIcon,   color: '#66c0f4' },
    platforms.twitch?.displayName && { label: 'Twitch', display: platforms.twitch.displayName, Icon: TwitchIcon,  color: '#9146FF' },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      {items.map(({ label, display, Icon, color }) => (
        <span
          key={label}
          title={`${label}: ${display}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 12, fontWeight: 600, color,
            padding: '4px 12px', borderRadius: 20,
            border: `1px solid ${color}44`,
            background: `${color}11`,
          }}
        >
          <Icon size={13} />
          {display}
        </span>
      ))}
    </div>
  );
}

// Archetype visual metadata — shared with ArchetypeBadge
function SocialLinks({ links = {} }) {
  const items = [
    links.twitter && { label: 'Twitter', href: `https://x.com/${links.twitter}`, display: `@${links.twitter}`, color: '#1d9bf0' },
    links.twitch  && { label: 'Twitch',  href: `https://twitch.tv/${links.twitch}`, display: links.twitch, color: '#9146ff' },
    links.youtube && { label: 'YouTube', href: links.youtube, display: 'YouTube', color: '#ff0000' },
    links.discord && { label: 'Discord', href: null, display: links.discord, color: '#5865f2' },
    links.website && { label: 'Website', href: links.website, display: 'Portfolio', color: 'var(--text-secondary)' },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
      {items.map(({ label, href, display, color }) =>
        href ? (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12, fontWeight: 600, color,
              padding: '4px 12px', borderRadius: 20,
              border: `1px solid ${color}44`,
              background: `${color}11`,
              textDecoration: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = `${color}22`}
            onMouseLeave={e => e.currentTarget.style.background = `${color}11`}
          >
            {display}
          </a>
        ) : (
          <span
            key={label}
            title={`Discord: ${display}`}
            style={{
              fontSize: 12, fontWeight: 600, color,
              padding: '4px 12px', borderRadius: 20,
              border: `1px solid ${color}44`,
              background: `${color}11`,
              cursor: 'default',
            }}
          >
            {display}
          </span>
        )
      )}
    </div>
  );
}

/* ─── Archetype SVG Icons ──────────────────────────────────────────── */
function IconAnchor({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="2"/>
      <line x1="12" y1="7" x2="12" y2="20"/>
      <line x1="5" y1="11" x2="19" y2="11"/>
      <path d="M5 20c0-2.5 3-4 7-4s7 1.5 7 4"/>
    </svg>
  );
}
function IconBolt({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path d="M13 2 4 13h7l-1 9 10-11h-7z" fill={color}/>
    </svg>
  );
}
function IconFlame({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2c-2 4-5 6.5-5 11a5 5 0 0 0 10 0c0-4.5-3-7-5-11z"/>
      <path d="M10.5 17.5c.5-1 1.5-1.5 1.5-1.5s1 .5 1.5 1.5"/>
    </svg>
  );
}
function IconNeuralHub({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round">
      <circle cx="12" cy="12" r="3"/>
      <line x1="12" y1="3" x2="12" y2="9"/>
      <line x1="12" y1="15" x2="12" y2="21"/>
      <line x1="3" y1="12" x2="9" y2="12"/>
      <line x1="15" y1="12" x2="21" y2="12"/>
      <circle cx="12" cy="3" r="1.5" fill={color} stroke="none"/>
      <circle cx="12" cy="21" r="1.5" fill={color} stroke="none"/>
      <circle cx="3" cy="12" r="1.5" fill={color} stroke="none"/>
      <circle cx="21" cy="12" r="1.5" fill={color} stroke="none"/>
    </svg>
  );
}
function IconSprout({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="22" x2="12" y2="10"/>
      <path d="M12 10C10 7 6 6 4 8c0 0 0 6 8 5"/>
      <path d="M12 10C14 7 18 6 20 8c0 0 0 6-8 5"/>
    </svg>
  );
}
function IconScales({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21"/>
      <line x1="6" y1="21" x2="18" y2="21"/>
      <line x1="4" y1="8" x2="20" y2="8"/>
      <path d="M4 8l-2 5a2 2 0 0 0 4 0z"/>
      <path d="M20 8l-2 5a2 2 0 0 0 4 0z"/>
    </svg>
  );
}
function IconScope({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round">
      <circle cx="12" cy="12" r="8"/>
      <line x1="12" y1="2" x2="12" y2="6"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
      <line x1="2" y1="12" x2="6" y2="12"/>
      <line x1="18" y1="12" x2="22" y2="12"/>
      <circle cx="12" cy="12" r="2" fill={color} stroke="none"/>
    </svg>
  );
}
function IconCharge({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="7,5 14,12 7,19"/>
      <polyline points="13,5 20,12 13,19"/>
    </svg>
  );
}
function IconReticle({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round">
      <circle cx="12" cy="12" r="7"/>
      <line x1="12" y1="5" x2="12" y2="8.5"/>
      <line x1="12" y1="15.5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="8.5" y2="12"/>
      <line x1="15.5" y1="12" x2="19" y2="12"/>
      <circle cx="12" cy="12" r="1.5" fill={color} stroke="none"/>
    </svg>
  );
}
function IconEye({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
      <circle cx="12" cy="12" r="1" fill={color} stroke="none"/>
    </svg>
  );
}
function IconRisingBars({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <rect x="3" y="14" width="5" height="8" rx="1"/>
      <rect x="9.5" y="9" width="5" height="13" rx="1"/>
      <rect x="16" y="4" width="5" height="18" rx="1"/>
    </svg>
  );
}
function IconSkull({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 17C4 15.5 3 13 3 10.5 3 6 7 3 12 3s9 3 9 7.5c0 2.5-1 4.5-3 6"/>
      <path d="M9 17v3h6v-3"/>
      <line x1="10" y1="20" x2="10" y2="22"/>
      <line x1="14" y1="20" x2="14" y2="22"/>
      <circle cx="9" cy="11" r="1.5" fill={color} stroke="none"/>
      <circle cx="15" cy="11" r="1.5" fill={color} stroke="none"/>
    </svg>
  );
}
function IconSword({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="2" x2="12" y2="16"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <path d="M11 16h2l1 4H10z"/>
    </svg>
  );
}
function IconShieldPlus({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="12" y1="9" x2="12" y2="15"/>
    </svg>
  );
}
function IconCompass({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <polygon points="12,5 14.5,12 12,19 9.5,12" fill={color} stroke="none"/>
    </svg>
  );
}
function IconDagger({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="17"/>
      <path d="M9 10h6"/>
      <path d="M12 3L9.5 7h5z" fill={color} stroke="none"/>
      <path d="M10 17h4l-1 3h-2z" fill={color} stroke="none"/>
    </svg>
  );
}
function IconGem({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12l4 6-10 12L2 9z"/>
      <line x1="2" y1="9" x2="22" y2="9"/>
      <line x1="6" y1="3" x2="10" y2="9"/>
      <line x1="18" y1="3" x2="14" y2="9"/>
    </svg>
  );
}
function IconRotate({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6"/>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
      <path d="M3 22v-6h6"/>
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
    </svg>
  );
}

export const ARCHETYPE_META = {
  // General
  ANCHOR:      { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.25)', icon: IconAnchor },
  CLUTCH:      { color: '#ff6b35', glow: 'rgba(255, 107, 53, 0.25)', icon: IconBolt },
  FRAGGER:     { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.25)', icon: IconFlame },
  STRATEGIST:  { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.25)', icon: IconNeuralHub },
  RAW_TALENT:  { color: '#10b981', glow: 'rgba(16, 185, 129, 0.25)', icon: IconSprout },
  BALANCED:    { color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.25)', icon: IconScales },
  // FPS
  AWPER:          { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.25)', icon: IconScope },
  ENTRY:          { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.25)', icon: IconCharge },
  RIFLER:         { color: '#10b981', glow: 'rgba(16, 185, 129, 0.25)', icon: IconReticle },
  LURKER:         { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.25)', icon: IconEye },
  FPS_RAW_TALENT: { color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.25)', icon: IconRisingBars },
  FRAGMASTER:     { color: '#6b7280', glow: 'rgba(107, 114, 128, 0.25)', icon: IconSkull },
  // MOBA
  CARRY:          { color: '#ff6b35', glow: 'rgba(255, 107, 53, 0.25)', icon: IconSword },
  SUPPORT:        { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.25)', icon: IconShieldPlus },
  JUNGLER:        { color: '#10b981', glow: 'rgba(16, 185, 129, 0.25)', icon: IconCompass },
  MID_ASSASSIN:   { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.25)', icon: IconDagger },
  MOBA_RAW_TALENT:{ color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.25)', icon: IconGem },
  FLEX:           { color: '#6b7280', glow: 'rgba(107, 114, 128, 0.25)', icon: IconRotate },
};

export function ArchetypeIcon({ archetypeId, size = 16, color }) {
  const meta = ARCHETYPE_META[archetypeId];
  if (!meta || !meta.icon) return null;
  const Icon = meta.icon;
  return <Icon size={size} color={color || meta.color} />;
}

const DIMENSION_LABELS_BY_GENRE = {
  general: {
    rawSpeed:         'Raw Speed',
    decisionQuality:  'Decision Quality',
    aimPrecision:     'Aim Precision',
    trackingAccuracy: 'Tracking Accuracy',
    workingMemory:    'Working Memory',
    consistency:      'Consistency',
  },
  fps: {
    rawSpeed:         'Raw Speed',
    decisionQuality:  'Decision Quality',
    aimPrecision:     'Aim Precision',
    trackingAccuracy: 'Tracking Accuracy',
    flickSpeed:       'Flick Speed',
    consistency:      'Consistency',
  },
  moba: {
    rawSpeed:         'Raw Speed',
    decisionQuality:  'Decision Quality',
    prioritization:   'Target Prioritization',
    trackingAccuracy: 'Tracking Accuracy',
    workingMemory:    'Working Memory',
    consistency:      'Consistency',
  },
};

const DIMENSION_DESCRIPTIONS_BY_GENRE = {
  general: {
    rawSpeed:         'Scored from the Reaction test. You responded to green stimuli as fast as possible while ignoring red — your median response time across all correct trials converts to this percentile. Sub-200ms is elite; most players land between 220–280ms. In-game this is the gap between winning and losing a duel at equal skill.',
    decisionQuality:  'Scored from the Composure test. Five arrows appeared at once — you had to identify the centre arrow while ignoring the flanking ones, with distractions firing mid-trial. Accuracy and speed under interference both count. High scorers make correct calls fast even when the environment is noisy.',
    aimPrecision:     'Scored from the Aim test. 25 targets appeared across the full screen — your score reflects hit rate, how close to the centre of each target you clicked, and how quickly you acquired each one. High scorers are mechanically accurate at pace across all screen positions, not just comfortable angles.',
    trackingAccuracy: 'Scored from the Tracking test. You kept your cursor inside a ring that moved in an unpredictable Lissajous path, starting slow and accelerating. Your score is the percentage of time you stayed on target. This reflects the sustained crosshair control needed to track opponents through movement.',
    workingMemory:    'Scored from the Memory test. A 3×3 grid flashed sequences of increasing length — you had to recall and replay each one in order. Sequences ranged from 3 to 6 cells. High scorers hold more information accurately under load, which maps to tracking cooldowns, positions, and game state simultaneously.',
    consistency:      'Derived from variance across all your test modules. A player who performs at the same level from module one to six scores higher than someone who spikes early then degrades. High consistency under fatigue is the difference between a reliable teammate and a player who disappears in the third quarter.',
  },
  fps: {
    rawSpeed:         'Scored from the Reaction test. You responded to green stimuli as fast as possible while ignoring red — your median response time across all correct trials converts to this percentile. Sub-200ms is elite; most players land between 220–280ms. In-game this is the gap between winning and losing a duel at equal skill.',
    decisionQuality:  'Scored from the Composure test. Five arrows appeared at once — you had to identify the centre arrow while ignoring the flanking ones, with distractions firing mid-trial. Accuracy and speed under interference both count. High scorers make correct calls fast even when the environment is noisy.',
    aimPrecision:     'Scored from the Aim test. 25 targets appeared across the full screen — your score reflects hit rate, how close to the centre of each target you clicked, and how quickly you acquired each one. High scorers are mechanically accurate at pace across all screen positions, not just comfortable angles.',
    trackingAccuracy: 'Scored from the Tracking test. You kept your cursor inside a ring that moved in an unpredictable Lissajous path, starting slow and accelerating. Your score is the percentage of time you stayed on target. This reflects the sustained crosshair control needed to track opponents through movement.',
    flickSpeed:       'Scored from the Flicker test. Targets appeared and vanished in 200ms — you had to click within that window. Your score combines hit rate and acquisition speed across 30 trials. This is the closest the combine gets to measuring raw flick shot ability — the snap that separates good aimers from great ones.',
    consistency:      'Derived from variance across all your test modules. A player who performs at the same level from module one to six scores higher than someone who spikes early then degrades. High consistency under fatigue is the difference between a reliable teammate and a player who disappears in the third quarter.',
  },
  moba: {
    rawSpeed:         'Scored from the Reaction test. You responded to green stimuli as fast as possible while ignoring red — your median response time across all correct trials converts to this percentile. Sub-200ms is elite; most players land between 220–280ms. In-game this is the gap between landing a flash or missing the window entirely.',
    decisionQuality:  'Scored from the Composure test. Five arrows appeared at once — you had to identify the centre arrow while ignoring the flanking ones, with distractions firing mid-trial. Accuracy under interference counts as much as speed. High scorers make correct calls even in chaotic team fights.',
    prioritization:   'Scored from the Sequencing test. Multiple targets appeared simultaneously — you had to click them in the correct order as fast as possible across rounds of increasing complexity. Directly reflects the target prioritisation decisions that define high-level MOBA play: who do you hit first, and how fast.',
    trackingAccuracy: 'Scored from the Tracking test. You kept your cursor inside a ring moving in an unpredictable accelerating path. Your score is time-on-target percentage. In MOBA terms this reflects the cursor precision needed for skill shots, orbwalking, and tracking mobile opponents across fog of war.',
    workingMemory:    'Scored from the Memory test. A 3×3 grid flashed sequences of increasing length — you had to recall and replay each one in order. High scorers hold more information accurately under load, mapping directly to cooldown tracking, minimap awareness, and managing multiple objectives simultaneously.',
    consistency:      'Derived from variance across all your test modules. A player who performs at the same level from module one to six scores higher than someone who spikes early then degrades. High consistency under fatigue is the difference between a reliable teammate and a player who disappears in the third quarter.',
  },
};

const TIER_LABELS = [
  { min: 90, label: 'Elite',      color: '#00d4ff' },
  { min: 75, label: 'Strong',     color: '#10b981' },
  { min: 50, label: 'Average',    color: '#f59e0b' },
  { min: 25, label: 'Below Avg',  color: '#ef4444' },
  { min: 0,  label: 'Low',        color: '#6b7280' },
];

function getTier(score) {
  return TIER_LABELS.find((t) => score >= t.min) || TIER_LABELS[TIER_LABELS.length - 1];
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function ScoreBar({ label, score, color, description, animDelay = 0 }) {
  const [showTip, setShowTip] = useState(false);
  const [showPercentile, setShowPercentile] = useState(false);
  const tier = getTier(score);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{label}</span>
          <button
            onClick={() => setShowTip(!showTip)}
            style={{
              background: showTip ? 'var(--accent-dim)' : 'none',
              border: '1px solid var(--border)',
              color: showTip ? 'var(--accent)' : 'var(--text-muted)',
              cursor: 'pointer', fontSize: 11, lineHeight: 1,
              width: 18, height: 18, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >?</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: animDelay + 0.4 }}
            onMouseEnter={() => setShowPercentile(true)}
            onMouseLeave={() => setShowPercentile(false)}
            style={{ fontSize: 12, color: tier.color, fontWeight: 700, cursor: 'default' }}
          >
            {showPercentile ? `${ordinal(score)} percentile` : tier.label}
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: animDelay + 0.4 }}
            style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', minWidth: 28, textAlign: 'right' }}
          >
            {score}
          </motion.span>
        </div>
      </div>

      {showTip && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.6, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, borderLeft: '2px solid var(--accent)' }}>{description}</p>
      )}

      <div className="progress-bar">
        <motion.div
          className="progress-bar__fill"
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94], delay: animDelay }}
          style={{ background: color, boxShadow: `0 0 10px ${color}55` }}
        />
      </div>
    </div>
  );
}

// Counts up from 0 to target
function useCountUp(target, startDelay = 0, durationMs = 900) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!target) return;
    let frame;
    const startAt = performance.now() + startDelay * 1000;
    const tick = (now) => {
      const elapsed = Math.max(0, now - startAt);
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - (1 - progress) * (1 - progress);
      setDisplay(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, startDelay, durationMs]);
  return display;
}

export default function NeuralProfile() {
  const { shareableId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [deepStats, setDeepStats] = useState(null);
  const [rankComparison, setRankComparison] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [coaching, setCoaching] = useState(null);
  const [coachingPending, setCoachingPending] = useState(false);
  const [myReferralCode, setMyReferralCode] = useState('');
  const [refShareCopied, setRefShareCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyChallenge = () => {
    const url = `${window.location.origin}/challenge/${shareableId}`;
    navigator.clipboard.writeText(url).then(() => {
      setChallengeCopied(true);
      setTimeout(() => setChallengeCopied(false), 2000);
    });
  };

  const shareOnX = () => {
    if (!profile) return;
    const score = profile.scores?.overallScore ?? '?';
    const archetypeName = profile.archetype?.name || 'Unknown';
    const DIM_LABELS = { rawSpeed: 'Reaction', decisionQuality: 'Decision', aimPrecision: 'Aim', trackingAccuracy: 'Tracking', workingMemory: 'Memory', consistency: 'Consistency', flickSpeed: 'Flick Speed', prioritization: 'Prioritization' };
    const dims = profile.scores?.dimensions || {};
    const topDim = Object.entries(dims).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))[0];
    const topStr = topDim ? ` ${DIM_LABELS[topDim[0]] || topDim[0]}: ${topDim[1]}.` : '';
    const text = `${archetypeName}. ${score}/100 on @NeuroRankApp.${topStr} Think you can top it?`;
    const url = window.location.href;
    window.posthog?.capture('profile_shared', { platform: 'twitter', shareableId, archetype: profile.archetype?.id, overallScore: score });
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener');
  };

  const isBookmarked = user?.bookmarks?.includes(shareableId);

  const handleBookmark = async () => {
    if (!user) return;
    try {
      await toggleBookmark(shareableId);
      await refreshUser();
    } catch {
      alert('Failed to update bookmark.');
    }
  };


  useEffect(() => {
    getProfile(shareableId)
      .then((p) => {
        setProfile(p);
        document.title = `${p.playerTag} — ${p.archetype?.name || 'NeuroRank'} | NeuroRank`;
        try { window.posthog?.capture('profile_viewed', { shareableId, archetype: p.archetype?.id, genre: p.genre }); } catch {}
        import('../services/track').then(({ track }) => track('profile_view', { shareableId, archetype: p.archetype?.id, genre: p.genre }));
        // Fetch run history for this player tag
        getProfilesByTag(p.playerTag)
          .then(({ profiles }) => setHistory(profiles.filter((h) => h.shareableId !== shareableId)))
          .catch(() => {});
      })
      .catch(() => setError('Profile not found.'))
      .finally(() => setLoading(false));
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, [shareableId]);

  const isClaimed = user?.claimedProfiles?.includes(shareableId);

  useEffect(() => {
    if (!isClaimed) return;
    getReferralInfo().then(info => { if (info?.referralCode) setMyReferralCode(info.referralCode); }).catch(() => {});
  }, [isClaimed]);

  useEffect(() => {
    if (!profile || !isClaimed || !profile.isPlayerPro) return;
    getRankComparison(shareableId).then(setRankComparison).catch(() => {});
    getDeepStats(shareableId).then(setDeepStats).catch(() => {});

    // Coaching: use cached value if already on profile, else poll
    if (profile.coaching) {
      setCoaching(profile.coaching);
    } else {
      setCoachingPending(true);
      let attempts = 0;
      const maxAttempts = 24; // poll up to 2 min (24 × 5s)
      const interval = setInterval(async () => {
        attempts++;
        try {
          const result = await getCoachingStatus(shareableId);
          if (result.status === 'ready') {
            setCoaching(result.coaching);
            setCoachingPending(false);
            clearInterval(interval);
          }
        } catch (_) {}
        if (attempts >= maxAttempts) {
          setCoachingPending(false);
          clearInterval(interval);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [profile, isClaimed, shareableId]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <p className="text-muted">Loading profile...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: 400, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 8 }}>Profile Not Found</h2>
          <p style={{ marginBottom: 24 }}>This profile ID doesn't exist or has been removed.</p>
          <button className="btn btn--primary" onClick={() => navigate('/')}>Take the Combine</button>
        </div>
      </div>
    );
  }

  const meta = ARCHETYPE_META[profile.archetype.id] || ARCHETYPE_META.BALANCED;
  const accentColor = (profile.isPlayerPro && profile.profileTheme?.accentColor) ? profile.profileTheme.accentColor : meta.color;
  const accentGlow  = (profile.isPlayerPro && profile.profileTheme?.accentColor) ? `${profile.profileTheme.accentColor}22` : meta.glow;
  const cardStyle   = (profile.isPlayerPro && profile.profileTheme?.cardStyle && profile.profileTheme.cardStyle !== 'default') ? profile.profileTheme.cardStyle : null;
  const cardStyleOverride = cardStyle === 'glass'   ? { backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.04)' }
                          : cardStyle === 'minimal' ? { background: '#0d1117', boxShadow: 'none', border: '1px solid #1e2530' }
                          : cardStyle === 'neon'    ? { boxShadow: `0 0 24px ${accentColor}44, 0 0 48px ${accentColor}18` }
                          : {};
  const { scores } = profile;
  const genre = profile.genre || 'general';
  const DIMENSION_LABELS = DIMENSION_LABELS_BY_GENRE[genre] || DIMENSION_LABELS_BY_GENRE.general;
  const DIMENSION_DESCRIPTIONS = DIMENSION_DESCRIPTIONS_BY_GENRE[genre] || DIMENSION_DESCRIPTIONS_BY_GENRE.general;

  const radarData = Object.entries(DIMENSION_LABELS)
    .filter(([key]) => scores[key] != null)
    .map(([key, label]) => ({
      dimension: label,
      score: scores[key],
    }));

  const overallScore = scores.overallScore !== undefined
    ? scores.overallScore
    : Math.round(
        Object.entries(scores)
          .filter(([k]) => k !== 'overallScore')
          .map(([, v]) => v)
          .reduce((a, b) => a + b, 0) /
        Object.entries(scores).filter(([k]) => k !== 'overallScore').length
      );

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Print-only header — hidden on screen, visible when printing */}
      <div className="print-header" style={{
        display: 'none',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '2px solid #111', paddingBottom: 10, marginBottom: 18,
      }}>
        <span style={{ fontWeight: 900, fontSize: 20, letterSpacing: 3 }}>NEURORANK</span>
        <span style={{ fontSize: 12, color: '#555' }}>
          Neural Profile Report · {shareableId} · {new Date().toLocaleDateString()}
        </span>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="print-hero"
        style={{
          background: `linear-gradient(180deg, ${accentGlow} 0%, transparent 100%)`,
          borderBottom: '1px solid var(--border)',
          padding: '48px 0 32px',
          textAlign: 'center',
        }}
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            style={{ marginBottom: 12 }}
          >
            <ArchetypeBadge archetypeId={profile.archetype.id} archetypeName={profile.archetype.name} size="lg" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.12 }}
            style={{ fontSize: 36, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {profile.isPlayerPro && (
              <svg width={22} height={22} viewBox="0 0 24 24" fill="#f59e0b" style={{ flexShrink: 0, marginBottom: 2 }}>
                <path d="M5 16L2 6l5 4 5-8 5 8 5-4-3 10H5z"/>
              </svg>
            )}
            {profile.playerTag}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: profile.bio ? 12 : 20, flexWrap: 'wrap' }}
          >
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
              Profile ID: <span className="mono text-accent">{shareableId}</span>
            </p>
            {profile.genre && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                padding: '3px 10px', borderRadius: 20,
                border: '1px solid var(--accent)',
                color: 'var(--accent)',
                textTransform: 'uppercase',
              }}>
                {profile.genre} COMBINE
              </span>
            )}
            {profile.claimed && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                padding: '3px 10px', borderRadius: 20,
                border: '1px solid #10b98155',
                background: '#10b98111',
                color: '#10b981',
              }}>
                ✓ Verified Player
              </span>
            )}
            {profile.isFeatured && profile.featuredUntil > Date.now() && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                padding: '3px 10px', borderRadius: 20,
                border: '1px solid #f59e0b55',
                background: '#f59e0b11',
                color: '#f59e0b',
              }}>
                ★ Featured
              </span>
            )}
          </motion.div>

          {/* Game context row */}
          {(profile.game || profile.rank || profile.age || profile.region) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.22 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}
            >
              {profile.game && (
                <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                  {profile.game}
                </span>
              )}
              {profile.game && profile.rank && (
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>·</span>
              )}
              {profile.rank && (
                <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700 }}>
                  {profile.rank}
                </span>
              )}
              {(profile.game || profile.rank) && (profile.age || profile.region) && (
                <span style={{ color: 'var(--border)', fontSize: 12 }}>|</span>
              )}
              {profile.age && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Age {profile.age}</span>
              )}
              {profile.region && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                }}>
                  {profile.region}
                </span>
              )}
            </motion.div>
          )}

          {/* Bio */}
          {profile.bio && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.24 }}
              style={{
                fontSize: 14, color: 'var(--text-secondary)', maxWidth: 480,
                margin: '0 auto 12px', lineHeight: 1.6, textAlign: 'center',
              }}
            >
              {profile.bio}
            </motion.p>
          )}

          {/* Platform badges */}
          {profile.linkedPlatforms && Object.keys(profile.linkedPlatforms).length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.24 }}
            >
              <PlatformBadges platforms={profile.linkedPlatforms} />
            </motion.div>
          )}

          {/* Social links */}
          {profile.socialLinks && Object.values(profile.socialLinks).some(Boolean) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.26 }}
            >
              <SocialLinks links={profile.socialLinks} />
            </motion.div>
          )}

          {/* Cinematic overall score */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
            style={{ marginTop: 28 }}
          >
            <OverallCounter target={overallScore} color={accentColor} glow={accentGlow} />
          </motion.div>
        </div>
      </motion.div>

      {/* Tab nav — only shown for claimed Pro profiles */}
      {isClaimed && profile.isPlayerPro && (
        <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)', position: 'sticky', top: 0, zIndex: 10 }}>
          <div className="container" style={{ display: 'flex', gap: 0 }}>
            {['profile', 'pro'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '12px 20px', fontSize: 13, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: activeTab === tab ? '#f59e0b' : 'var(--text-muted)',
                  borderBottom: `2px solid ${activeTab === tab ? '#f59e0b' : 'transparent'}`,
                  marginBottom: -1, transition: 'color 0.15s',
                }}
              >
                {tab === 'profile' ? 'Profile' : '⚡ Pro Tools'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="container" style={{ paddingTop: 40 }}>
        {/* Profile tab wrapper — hidden when Pro tab is active */}
        <div style={{ display: isClaimed && profile.isPlayerPro && activeTab === 'pro' ? 'none' : undefined }}>

        {/* Achievement Badges */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.28 }}
          className="card no-print"
          style={{ marginBottom: 24 }}
        >
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Achievements</h3>
          <ProfileBadges
            scores={scores}
            badgeContext={isClaimed ? {
              combineCount: user?.claimedProfiles?.length || 0,
              completedGenres: user?.completedGenres || [],
              referralCount: user?.referralCount || 0,
            } : null}
          />
        </motion.div>

        <div className="print-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* Radar Chart */}
          <motion.div
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <h3 style={{ marginBottom: 20 }}>Neural Profile</h3>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#1f2937" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: '#8b9ab5', fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#4b5a72', fontSize: 10 }} />
                <Radar
                  name={profile.playerTag}
                  dataKey="score"
                  stroke={accentColor}
                  fill={accentColor}
                  fillOpacity={0.2}
                  strokeWidth={2}
                  isAnimationActive={true}
                  animationBegin={400}
                  animationDuration={800}
                />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                  labelStyle={{ color: 'var(--text-primary)' }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Score Bars */}
          <motion.div
            className="card"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <h3 style={{ marginBottom: 20 }}>Dimension Breakdown</h3>
            {Object.entries(DIMENSION_LABELS).map(([key, label], i) => (
              <ScoreBar
                key={key}
                label={label}
                score={scores[key] || 0}
                color={accentColor}
                description={DIMENSION_DESCRIPTIONS[key]}
                animDelay={0.35 + i * 0.08}
              />
            ))}
          </motion.div>
        </div>

        {/* Archetype Insight */}
        {(profile.archetype?.description || profile.archetype?.roles?.length > 0) && (
          <motion.div
            className="card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.55 }}
            style={{ marginTop: 24, borderColor: `${accentColor}44` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: `${accentColor}15`, border: `1px solid ${accentColor}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ArchetypeIcon archetypeId={profile.archetype.id} size={24} />
              </div>
              <div>
                <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, margin: 0 }}>
                  Archetype Profile
                </p>
                <h3 style={{ margin: '3px 0 0', fontSize: 17, fontWeight: 800, color: accentColor }}>
                  {profile.archetype.name}
                </h3>
              </div>
            </div>
            {profile.archetype.description && (
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: profile.archetype.roles?.length ? 16 : 0 }}>
                {profile.archetype.description}
              </p>
            )}
            {profile.archetype.roles?.length > 0 && (
              <div>
                <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: 10 }}>
                  Recommended Roles
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {profile.archetype.roles.map((role) => (
                    <span key={role} style={{
                      fontSize: 12, fontWeight: 600,
                      padding: '5px 12px', borderRadius: 20,
                      border: `1px solid ${accentColor}44`,
                      background: `${accentColor}0d`,
                      color: 'var(--text-secondary)',
                    }}>
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* AI Report — gated behind auth for non-owners */}
        {profile.report && (() => {
          const isOwner = user && (isClaimed || user.claimedProfiles?.includes(shareableId));
          const showFull = !!user;
          const preview = profile.report.split(/(?<=[.!?])\s+/)[0] || '';
          return (
            <motion.div
              className="card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              style={{ marginTop: 24 }}
            >
              <h3 style={{ marginBottom: 16 }}>Neural Profile Report</h3>
              <div style={{
                borderLeft: `3px solid ${accentColor}`,
                paddingLeft: 20,
                color: 'var(--text-secondary)',
                lineHeight: 1.85,
                whiteSpace: 'pre-wrap',
                position: 'relative',
              }}>
                {showFull ? profile.report : preview}
                {!showFull && (
                  <div style={{
                    position: 'relative', marginTop: -40, paddingTop: 60,
                    background: 'linear-gradient(transparent, var(--bg-card) 70%)',
                    textAlign: 'center', paddingBottom: 8,
                  }}>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                      Create a free account to read your full scouting report
                    </p>
                    <button
                      className="btn btn--primary"
                      style={{ fontSize: 13, padding: '10px 28px' }}
                      onClick={() => setShowAuth(true)}
                    >
                      Sign Up — It's Free
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })()}

        {/* Discord CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          style={{ marginTop: 16, textAlign: 'center' }}
        >
          <a
            href="https://discord.gg/W9Ftc6TC8J"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: 10,
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
              background: 'rgba(88,101,242,0.08)', border: '1px solid rgba(88,101,242,0.25)',
              color: '#5865F2', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(88,101,242,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(88,101,242,0.08)'}
          >
            Compare your archetype with other players → Join the Discord
          </a>
        </motion.div>

        </div>{/* end profile tab wrapper */}

        {/* Pro tab wrapper — hidden when Profile tab is active */}
        <div style={{ display: isClaimed && profile.isPlayerPro && activeTab === 'profile' ? 'none' : undefined }}>

        {/* Player Pro — locked preview or real content */}
        {isClaimed && profile.isPlayerPro ? (
          <>
            {rankComparison && (
              <motion.div className="card no-print" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.62 }} style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>⚡ Player Pro</span>
                  <h3 style={{ margin: 0 }}>Rank Benchmarks</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #1a1a1a' }}>Dimension</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', color: accentColor, fontWeight: 700, borderBottom: '1px solid #1a1a1a' }}>You</th>
                        {Object.keys(rankComparison.tiers).map(tier => (
                          <th key={tier} style={{ textAlign: 'center', padding: '6px 8px', color: '#555', fontWeight: 600, borderBottom: '1px solid #1a1a1a' }}>{tier}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(rankComparison.playerScores).map(([dim, score]) => {
                        const tierNames = Object.keys(rankComparison.tiers);
                        const playerTier = tierNames.filter(t => score >= rankComparison.tiers[t][dim]).pop();
                        return (
                          <tr key={dim} style={{ borderBottom: '1px solid #111' }}>
                            <td style={{ padding: '7px 8px', color: '#aaa', fontWeight: 600 }}>{dim.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</td>
                            <td style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 800, color: accentColor }}>{score}</td>
                            {tierNames.map(tier => (
                              <td key={tier} style={{ textAlign: 'center', padding: '7px 8px', color: tier === playerTier ? '#fff' : '#444', background: tier === playerTier ? `${accentColor}22` : 'transparent', fontWeight: tier === playerTier ? 700 : 400 }}>
                                {rankComparison.tiers[tier][dim] ?? '—'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {deepStats && (
              <motion.div className="card no-print" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.64 }} style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>⚡ Player Pro</span>
                  <h3 style={{ margin: 0 }}>Deep Stats</h3>
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  {[['Total Trials', deepStats.totalTrials], ['Accuracy', deepStats.overallAccuracy != null ? `${deepStats.overallAccuracy}%` : null], ['On-Target', deepStats.trackingStats ? `${deepStats.trackingStats.avgTimeOnTarget}%` : null]].filter(([, v]) => v != null).map(([label, val]) => (
                    <div key={label} style={{ background: '#111', borderRadius: 8, padding: '10px 16px', minWidth: 90 }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: accentColor }}>{val}</div>
                      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
                    </div>
                  ))}
                </div>
                {deepStats.phaseBreakdown?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#555', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Phase Breakdown</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {deepStats.phaseBreakdown.map((p, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#111', borderRadius: 6, fontSize: 12 }}>
                          <span style={{ color: '#888', textTransform: 'capitalize' }}>{p.module} · {p.phase}</span>
                          <span style={{ display: 'flex', gap: 16, color: '#ccc' }}>
                            {p.avgRT != null && <span><span style={{ color: '#555' }}>RT </span>{p.avgRT}ms</span>}
                            <span><span style={{ color: '#555' }}>Acc </span>{p.accuracy}%</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {deepStats.reactionDistribution?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: '#555', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Reaction Time Distribution</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 48 }}>
                      {(() => { const max = Math.max(...deepStats.reactionDistribution.map(b => b.count)); return deepStats.reactionDistribution.map((b, i) => <div key={i} title={`${b.ms}ms: ${b.count}`} style={{ flex: 1, minWidth: 4, height: `${Math.max(8, (b.count / max) * 48)}px`, background: `${meta.color}99`, borderRadius: '3px 3px 0 0' }} />); })()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#444', marginTop: 4 }}>
                      <span>{deepStats.reactionDistribution[0]?.ms}ms</span>
                      <span>{deepStats.reactionDistribution[deepStats.reactionDistribution.length - 1]?.ms}ms</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* AI Coaching Report */}
            <motion.div className="card no-print" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.64 }} style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>⚡ Player Pro</span>
                <h3 style={{ margin: 0 }}>AI Coaching Report</h3>
                {coaching && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#555' }}>{coaching.tier === 'pro' ? 'Pro Analysis' : 'Standard Analysis'}</span>}
              </div>
              {coaching ? (
                <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{coaching.text}</div>
              ) : coachingPending ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#555', fontSize: 13, padding: '12px 0' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
                  </svg>
                  Generating your coaching report… this takes about 30 seconds.
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#444', fontStyle: 'italic' }}>
                  Complete a combine while logged in to generate your coaching report.
                </div>
              )}
            </motion.div>

            <motion.div className="card no-print" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.68 }} style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>⚡ Player Pro</span>
                <h3 style={{ margin: 0 }}>AI Training Plan</h3>
              </div>
              <TrainingPlan shareableId={shareableId} />
            </motion.div>
          </>
        ) : (
          <motion.div
            className="card no-print"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.62 }}
            style={{ marginTop: 24, position: 'relative', overflow: 'hidden', border: '1px solid #f59e0b22' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>⚡ Player Pro</span>
              <span style={{ fontSize: 11, color: '#444', fontWeight: 600 }}>$9.99/mo</span>
            </div>
            <div className="pro-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.55 }}>
              <div style={{ background: '#0d0d0d', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Rank Benchmarks</div>
                {[['Raw Speed', 72, 'Diamond'], ['Decision', 58, 'Platinum'], ['Aim', 61, 'Diamond'], ['Tracking', 55, 'Platinum']].map(([d, v, tier]) => (
                  <div key={d} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #1a1a1a', fontSize: 11 }}>
                    <span style={{ color: '#666' }}>{d}</span>
                    <span style={{ display: 'flex', gap: 8 }}><span style={{ fontWeight: 700, color: accentColor }}>{v}</span><span style={{ color: '#333', fontSize: 10 }}>{tier}</span></span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#0d0d0d', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Deep Stats</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {[['84', 'Trials'], ['91%', 'Acc']].map(([v, l]) => (
                    <div key={l} style={{ flex: 1, background: '#111', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: accentColor }}>{v}</div>
                      <div style={{ fontSize: 10, color: '#555' }}>{l}</div>
                    </div>
                  ))}
                </div>
                {[['composure · baseline', '94%'], ['composure · stress', '79%'], ['tilt · failure', '71%']].map(([p, a]) => (
                  <div key={p} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '3px 0', borderBottom: '1px solid #1a1a1a', color: '#555' }}>
                    <span style={{ textTransform: 'capitalize' }}>{p}</span><span style={{ color: '#444' }}>{a}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#0d0d0d', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>AI Training Plan</div>
                {[['Tilt Resistance', 41, 'Pressure Decision Drill'], ['Decision Quality', 54, 'Choice Reaction Ladder'], ['Composure', 63, 'Flanker Endurance Sets']].map(([dim, score, drill]) => (
                  <div key={dim} style={{ marginBottom: 8, background: '#111', borderRadius: 6, padding: '6px 8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>{dim}</span>
                      <span style={{ fontSize: 10, color: '#444' }}>{score}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#555' }}>{drill}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 24, background: 'linear-gradient(to bottom, transparent 15%, rgba(10,10,10,0.95) 60%)', borderRadius: 'inherit' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 6 }}>
                <rect x="3" y="11" width="18" height="12" rx="2.5" fill="#f59e0b22" stroke="#f59e0b" strokeWidth="1.5"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1.5" fill="#f59e0b"/>
                <line x1="12" y1="17.5" x2="12" y2="19.5" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666', textAlign: 'center', maxWidth: 280 }}>
                {(() => {
                  if (!isClaimed) return 'Claim this profile and upgrade to Player Pro';
                  const dims = profile.scores?.dimensions ?? {};
                  const DLABELS = { rawSpeed:'Reaction Speed', aimPrecision:'Aim Precision', trackingAccuracy:'Tracking', workingMemory:'Memory', decisionQuality:'Decision Quality', consistency:'Composure', flickSpeed:'Flick Speed', prioritization:'Prioritization' };
                  const [wDim, wScore] = Object.entries(dims).sort(([,a],[,b])=>a-b)[0] || [];
                  const label = wDim ? DLABELS[wDim] || wDim : null;
                  return label
                    ? `Your ${label} is ${Math.round(wScore)}/100. Player Pro unlocks the AI training plan targeting this dimension + Deep Stats.`
                    : 'Upgrade to unlock rank benchmarks, deep stats & AI training';
                })()}
              </p>
              <a href="/pricing" style={{ padding: '8px 20px', background: '#f59e0b', color: '#000', fontWeight: 700, fontSize: 12, borderRadius: 7, textDecoration: 'none' }}>
                Upgrade to Player Pro — $9.99/mo
              </a>
            </div>
          </motion.div>
        )}

        </div>{/* end pro tab wrapper */}

        {/* Claimed profile settings */}
        {isClaimed && activeTab !== 'pro' && <ProfileSettings user={user} refreshUser={refreshUser} />}

        {/* Run History */}
        {history.length > 0 && (
          <motion.div
            className="card no-print"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.65 }}
            style={{ marginTop: 24 }}
          >
            <h3 style={{ marginBottom: 16 }}>Previous Runs</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {history.map((run, i) => {
                const overall = run.scores?.overallScore ?? null;
                const currentOverall = profile.scores?.overallScore ?? null;
                const delta = (overall != null && currentOverall != null) ? currentOverall - overall : null;
                const date = run.createdAt ? new Date(run.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                const runMeta = ARCHETYPE_META[run.archetype?.id] || { color: '#8b9ab5', icon: '◈' };
                return (
                  <a
                    key={run.shareableId}
                    href={`/profile/${run.shareableId}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', background: 'var(--bg-card)',
                      textDecoration: 'none', transition: 'background 0.12s',
                      gap: 12,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <ArchetypeIcon archetypeId={run.archetype?.id} size={18} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {run.archetype?.name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, textTransform: 'capitalize' }}>
                          {run.genre} combine · {date}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: runMeta.color, fontVariantNumeric: 'tabular-nums' }}>{overall ?? '—'}</div>
                        {delta != null && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: delta >= 0 ? '#10b981' : '#ef4444', marginTop: 1 }}>
                            {delta >= 0 ? `▲ +${delta}` : `▼ ${delta}`}
                          </div>
                        )}
                        {delta == null && <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase' }}>Overall</div>}
                      </div>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>→</span>
                    </div>
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Non-owner CTA — only shown to visitors who haven't taken the combine */}
        {!isClaimed && (
          <div className="no-print" style={{
            marginTop: 20, padding: '14px 20px', borderRadius: 12,
            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                Think you can beat {profile?.playerTag}?
              </p>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                {profile?.scores?.overallScore ? `They scored ${Math.round(profile.scores.overallScore)}/100 · ` : ''}Free · 10 minutes
              </p>
            </div>
            <button
              className="btn btn--primary"
              style={{ fontSize: 13, padding: '9px 22px', flexShrink: 0, background: '#ef4444' }}
              onClick={() => navigate(`/challenge/${shareableId}`)}
            >
              Accept the Challenge →
            </button>
          </div>
        )}

        {/* Actions — Primary: share/viral */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="no-print"
          style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn--primary" onClick={() => setShowCard(true)}>
              Share Profile
            </button>
            <button className="btn btn--ghost" onClick={shareOnX} style={{ borderColor: '#1d9bf044', color: '#1d9bf0' }}>
              Share on X
            </button>
            <button className="btn btn--ghost" onClick={copyChallenge} style={{ borderColor: '#ef444444', color: '#ef4444' }}>
              {challengeCopied ? '✓ Link Copied!' : 'Challenge Others'}
            </button>
            <button className="btn btn--ghost" onClick={copyLink}>
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
            {isClaimed && myReferralCode && (
              <button
                className="btn btn--ghost"
                style={{ borderColor: 'rgba(0,212,255,0.3)', color: '#00d4ff' }}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/?ref=${myReferralCode}`);
                  setRefShareCopied(true);
                  setTimeout(() => setRefShareCopied(false), 2000);
                }}
              >
                {refShareCopied ? '✓ Copied!' : 'Invite a Friend'}
              </button>
            )}
            {isClaimed && (
              <span style={{ fontSize: 13, color: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                ✓ Your Profile
              </span>
            )}
            {user && !isClaimed && (
              <button
                className="btn btn--ghost"
                onClick={handleBookmark}
                style={{ borderColor: isBookmarked ? '#f59e0b' : undefined, color: isBookmarked ? '#f59e0b' : undefined }}
              >
                {isBookmarked ? '★ Bookmarked' : '☆ Bookmark'}
              </button>
            )}
          </div>

          {/* Secondary: utility/nav — collapsed under "More" */}
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              className="btn btn--ghost"
              onClick={() => setShowMore(prev => !prev)}
              style={{ fontSize: 13, color: '#666', borderColor: '#333', padding: '6px 14px' }}
            >
              {showMore ? '▾ Less' : '▸ More'}
            </button>
            {showMore && (
              <div style={{
                display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginLeft: 10,
              }}>
                <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => user ? navigate('/') : setShowAuth(true)}>
                  {user ? 'Retake Combine' : 'Sign In to Retake'}
                </button>
                <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => navigate('/leaderboard')}>
                  Leaderboard
                </button>
                <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => navigate('/scout')}>
                  Scout Dashboard
                </button>
                <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>
                  Export PDF
                </button>
                {isClaimed && !(profile.isFeatured && profile.featuredUntil > Date.now()) && (
                  <button
                    className="btn btn--ghost"
                    style={{ fontSize: 12, borderColor: '#f59e0b', color: '#f59e0b' }}
                    onClick={() => navigate(`/pricing#boosts`)}
                  >
                    ★ Boost Visibility
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

      {/* Share Card Modal */}
      {showCard && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24,
          }}
          onClick={() => setShowCard(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ProfileCard profile={profile} />
            <button
              className="btn btn--ghost"
              style={{ marginTop: 16, width: '100%' }}
              onClick={() => setShowCard(false)}
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const ROLES = ['', 'Entry Fragger', 'Support', 'IGL', 'Lurker', 'AWPer / Sniper', 'Flex', 'Jungler', 'Tank', 'Carry', 'Healer'];
const REGIONS = ['', 'NA', 'EU', 'APAC', 'LATAM', 'ME', 'Other'];
const GAMES = [
  '', 'Valorant', 'CS2', 'League of Legends', 'Dota 2', 'Overwatch 2',
  'Apex Legends', 'Rocket League', 'Fortnite', 'Rainbow Six Siege',
  'Call of Duty', 'Teamfight Tactics', 'Other',
];

function ProfileSettings({ user, refreshUser }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opp, setOpp] = useState(user?.openToOpportunities || false);
  const [role, setRole] = useState(user?.role || '');
  const [region, setRegion] = useState(user?.region || '');
  const [game, setGame] = useState(user?.game || '');
  const [rank, setRank] = useState(user?.rank || '');
  const [age, setAge] = useState(user?.age || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMyProfile({ openToOpportunities: opp, role, region, game, rank, age: age || null, bio });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.65 }}
      style={{ marginTop: 24 }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <h3 style={{ margin: 0 }}>My Profile Settings</h3>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{open ? '▲ Hide' : '▼ Edit'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Bio */}
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              BIO <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({bio.length}/200)</span>
            </label>
            <textarea
              className="input"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 200))}
              placeholder="Tell scouts about yourself — your playstyle, experience, goals..."
              rows={3}
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {/* Game + Rank */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 160 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>PRIMARY GAME</label>
              <select
                className="input"
                value={game}
                onChange={(e) => setGame(e.target.value)}
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
              >
                {GAMES.map((g) => <option key={g} value={g}>{g || 'Not specified'}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>CURRENT RANK</label>
              <input
                className="input"
                type="text"
                value={rank}
                onChange={(e) => setRank(e.target.value.slice(0, 40))}
                placeholder="e.g. Diamond 2, Immortal"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
              />
            </div>
          </div>

          {/* Role + Region + Age */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>ROLE</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
              >
                {ROLES.map((r) => <option key={r} value={r}>{r || 'Not specified'}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>REGION</label>
              <select
                className="input"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
              >
                {REGIONS.map((r) => <option key={r} value={r}>{r || 'Not specified'}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 100 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>AGE</label>
              <input
                className="input"
                type="number"
                min={13}
                max={99}
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="—"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', width: '100%' }}
              />
            </div>
          </div>

          {/* Open to opportunities toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Open to Opportunities</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                Signals to scouts that you're available for recruitment.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpp((v) => !v)}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: opp ? 'var(--accent)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: opp ? 22 : 2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
              }} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="btn btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          <LinkedPlatforms />
        </div>
      )}
    </motion.div>
  );
}

// Isolated counter component — cinematic large format
function OverallCounter({ target, color, glow }) {
  const display = useCountUp(target, 0.4, 1800);
  return (
    <div style={{ position: 'relative', display: 'inline-block', textAlign: 'center', padding: '12px 0' }}>
      {/* Radial glow bloom */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 240, height: 240,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${glow || 'rgba(0,212,255,0.25)'} 0%, transparent 65%)`,
        pointerEvents: 'none',
        animation: 'pulse 3s ease-in-out infinite',
      }} />
      <span style={{
        display: 'block',
        fontSize: 'clamp(80px, 12vw, 112px)',
        fontWeight: 900,
        color,
        lineHeight: 1,
        letterSpacing: '-0.05em',
        position: 'relative',
        fontFamily: 'var(--font-body)',
        textShadow: `0 0 80px ${color}66, 0 0 24px ${color}33`,
      }}>
        {display}
      </span>
      <span style={{
        display: 'block',
        fontSize: 11,
        color: 'var(--text-muted)',
        fontWeight: 700,
        letterSpacing: '3px',
        textTransform: 'uppercase',
        fontFamily: 'var(--font-mono)',
        marginTop: 6,
        position: 'relative',
      }}>
        / 100 · Cognitive Score
      </span>
      {target >= 50 && (
        <span style={{
          display: 'inline-block',
          marginTop: 10,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: target >= 90 ? '#10b981' : target >= 75 ? color : target >= 60 ? '#f59e0b' : 'var(--text-muted)',
          background: target >= 90 ? '#10b98118' : target >= 75 ? `${color}18` : target >= 60 ? '#f59e0b18' : 'transparent',
          border: `1px solid ${target >= 90 ? '#10b98144' : target >= 75 ? `${color}44` : target >= 60 ? '#f59e0b44' : 'transparent'}`,
          padding: '3px 12px',
          borderRadius: 20,
          position: 'relative',
        }}>
          {target >= 90 ? '★ Elite — Top 10%' : target >= 75 ? `Top 25%` : target >= 60 ? 'Top 40%' : 'Top 50%'}
        </span>
      )}
    </div>
  );
}
